import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Trash2, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/City/CityList.css"; // Your custom styles
const API_BASE_URL = process.env.REACT_APP_API_URL;
const CityList = () => {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Permissions
  const userPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const canRead = userPermissions.includes("read_City");
  const canCreate = userPermissions.includes("create_City");
  const canEdit = userPermissions.includes("update_City");
  const canDelete = userPermissions.includes("delete_City");

  // Fetch Cities
  const fetchCities = useCallback(async () => {
    if (!canRead) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/cities?search=${search}`);
      setCities(res.data);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  }, [search, canRead]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const toggleStatus = async (id) => {
    if (!canEdit) return;
    try {
      await axios.put(`${API_BASE_URL}/api/cities/status/${id}`);
      fetchCities();
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const deleteCity = async (id) => {
    if (!canDelete) return;
    if (window.confirm("Delete this city?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/cities/${id}`);
        fetchCities();
      } catch (error) {
        console.error("Error deleting city:", error);
      }
    }
  };

  const handleAddCity = () => {
    if (canCreate) navigate("/add-city");
  };

  const handleEditCity = (id) => {
    if (canEdit) navigate(`/edit-city/${id}`);
  };

  // If no read permission, show access denied
  if (!canRead) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">🚫 You do not have permission to view this page.</h4>
      </div>
    );
  }

  // Tooltip text for disabled buttons
  const noCreateMsg = "You do not have permission to add a city.";
  const noEditMsg = "You do not have permission to edit a city.";
  const noDeleteMsg = "You do not have permission to delete a city.";

  return (
    <div className="container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h2 className="mb-2 mb-md-0" style={{ color: "#102234" }}>City List</h2>
        <button
          className="btn shadow custom-blue-btn"
          onClick={handleAddCity}
          disabled={!canCreate}
          title={!canCreate ? noCreateMsg : "Add a new city"}
          aria-disabled={!canCreate}
          aria-label="Add City"
        >
          + Add City
        </button>
      </div>

      <div className="mb-3">
        <input
          type="search"
          placeholder="🔍 Search by City name"
          className="form-control"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search cities"
        />
      </div>

      <div className="table-responsive shadow rounded bg-white">
        <table className="table table-bordered table-striped mb-0">
          <thead className="table-light">
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Code</th>
              <th>State</th>
              <th>Country</th>
              <th>Remarks</th>
              <th className="text-center">Status</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {cities.length > 0 ? (
              cities.map((city, index) => (
                <tr key={city._id}>
                  <td>{index + 1}</td>
                  <td>{city.name}</td>
                  <td>{city.code}</td>
                  <td>{city.state?.name || "-"}</td>
                  <td>{city.country?.name || "-"}</td>
                  <td>{city.remarks || "-"}</td>
                  <td className="text-center">
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        className={`form-check-input ${city.status ? "switch-on" : "switch-off"}`}
                        type="checkbox"
                        role="switch"
                        checked={city.status}
                        onChange={() => toggleStatus(city._id)}
                        aria-label={`Toggle status of ${city.name}`}
                        disabled={!canEdit}
                        title={!canEdit ? noEditMsg : `Toggle status of ${city.name}`}
                        aria-disabled={!canEdit}
                      />
                    </div>
                  </td>
                  <td className="d-flex justify-content-center gap-2">
                    <button
                      onClick={() => handleEditCity(city._id)}
                      className="btn btn-sm btn-outline-primary"
                      title={!canEdit ? noEditMsg : `Edit ${city.name}`}
                      aria-label={`Edit ${city.name}`}
                      disabled={!canEdit}
                      aria-disabled={!canEdit}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => deleteCity(city._id)}
                      className="btn btn-sm btn-outline-danger"
                      title={!canDelete ? noDeleteMsg : `Delete ${city.name}`}
                      aria-label={`Delete ${city.name}`}
                      disabled={!canDelete}
                      aria-disabled={!canDelete}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  No cities found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CityList;
